import { captureException } from '@sentry/vue'

import type { Markup } from '@/types/markups'
import { MarkupType } from '@/types/markups'
import type { Line, Shape, Route, Track, Waypoint } from '@onxmaps/markups-web'

/**
 * *(AI-generated)*
 * Returns true if the given markup is a waypoint.
 */
export const isWaypoint = (markup: Markup): markup is Waypoint =>
  markup.type === MarkupType.waypoint
/**
 * *(AI-generated)*
 * Returns true if the given markup is a line.
 */
export const isLine = (markup: Markup): markup is Line =>
  markup.type === MarkupType.line
/**
 * *(AI-generated)*
 * Returns true if the given markup is a track.
 */
export const isTrack = (markup: Markup): markup is Track =>
  markup.type === MarkupType.track
/**
 * *(AI-generated)*
 * Returns true if the given markup is a shape.
 */
export const isShape = (markup: Markup): markup is Shape =>
  markup.type === MarkupType.shape
/**
 * *(AI-generated)*
 * Returns true if the given markup is a route.
 */
export const isRoute = (markup: Markup): markup is Route =>
  markup.type === MarkupType.route

/**
 * Returns the specific {@link MarkupType} of the given markup object.
 *
 * @param markup - The markup object to evaluate.
 * @returns The corresponding {@link MarkupType} if recognized, otherwise `undefined`.
 */
export const getMarkupType = (markup: Markup): MarkupType | undefined => {
  switch (markup?.type) {
    case MarkupType.waypoint:
      return MarkupType.waypoint
    case MarkupType.line:
      return MarkupType.line
    case MarkupType.track:
      return MarkupType.track
    case MarkupType.shape:
      return MarkupType.shape
    case MarkupType.route:
      return MarkupType.route
    default:
      return undefined
  }
}

/**
 * Captures markup-related errors with consistent tagging for Sentry
 *
 * @param error - The error to capture
 * @param operation - The operation that failed (e.g., 'create_line_failed', 'update_shape_failed')
 * @param extraData - Additional data to include with the error
 */
export function captureMarkupError(
  error: unknown,
  operation: string,
  extraData: Record<string, unknown> = {}
): void {
  const e = error instanceof Error ? error : new Error(String(error))

  captureException(e, {
    tags: {
      markups: operation,
    },
    extra: extraData,
  })
}
